import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { getMovement, updateMovement, getMaster, errorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, Truck, HardDrive } from 'lucide-react';

const MOVEMENT_TYPES = [
    { value: 'reception', label: 'Réception' },
    { value: 'expedition', label: 'Livraison' },
    { value: 'transfert', label: 'Transfert' },
];

const DELIVERY_MODES = [
    { value: 'none', label: 'Non renseigné' },
    { value: 'colissimo', label: 'Colissimo' },
    { value: 'chronopost', label: 'Chronopost' },
    { value: 'coursier', label: 'Coursier' },
    { value: 'la_poste', label: 'La Poste' },
    { value: 'autre', label: 'Autre' },
];

export default function MovementDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isManager } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);
    const [masterInfo, setMasterInfo] = useState(null);
    const [formData, setFormData] = useState({
        type: '',
        date: '',
        reception_address: '',
        expedition_address: '',
        delivery_mode: '',
        tracking_number: '',
        notes: '',
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when these inputs change; the loader is recreated each render and reads current state
    useEffect(() => { fetchMovement(); }, [id]);

    const fetchMovement = async () => {
        try {
            const resp = await getMovement(id);
            const mv = resp.data;
            setFormData({
                type: mv.type || '',
                date: mv.date || '',
                reception_address: mv.reception_address || '',
                expedition_address: mv.expedition_address || '',
                delivery_mode: mv.delivery_mode || 'none',
                tracking_number: mv.tracking_number || '',
                notes: mv.notes || '',
            });
            try {
                const masterResp = await getMaster(mv.master_id);
                setMasterInfo(masterResp.data);
            } catch {}
        } catch (error) {
            if (!error._isAuthError) {
                toast.error(errorMessage(error, 'Mouvement non trouvé'));
                navigate('/movements');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const dataToSend = {
                ...formData,
                delivery_mode: formData.delivery_mode === 'none' ? '' : formData.delivery_mode
            };
            await updateMovement(id, dataToSend);
            toast.success('Mouvement mis à jour');
            setEditing(false);
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la sauvegarde'));
        } finally {
            setSaving(false);
        }
    };

    const typeLabel = MOVEMENT_TYPES.find(t => t.value === formData.type)?.label || formData.type;
    const modeLabel = DELIVERY_MODES.find(m => m.value === formData.delivery_mode)?.label || formData.delivery_mode || 'Non renseigné';

    if (loading) {
        return (
            <Layout>
                <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-4">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-64" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="icon" aria-label="Retour" onClick={() => navigate(masterInfo ? `/masters/${masterInfo.id}` : '/movements')} data-testid="back-button">
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <h1 className="font-heading text-2xl font-bold">MOUVEMENT</h1>
                            <p className="text-sm text-muted-foreground">{typeLabel} — {formData.date}</p>
                        </div>
                    </div>
                    {!editing ? (
                        isManager() && (
                        <Button onClick={() => setEditing(true)} data-testid="edit-movement-button">
                            Modifier
                        </Button>
                        )
                    ) : (
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => { setEditing(false); fetchMovement(); }}>
                                Annuler
                            </Button>
                            <Button onClick={handleSave} disabled={saving} data-testid="save-movement-button">
                                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                Enregistrer
                            </Button>
                        </div>
                    )}
                </div>

                {/* Master Info */}
                {masterInfo && (
                    <Card className="bg-card border-border">
                        <CardContent className="p-4">
                            <Link
                                to={`/masters/${masterInfo.id}`}
                                className="flex items-center gap-3 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
                            >
                                <HardDrive className="w-5 h-5 text-muted-foreground" />
                                <div>
                                    <p className="font-medium text-sm">{masterInfo.name}</p>
                                    <p className="text-xs text-muted-foreground">Disque : {masterInfo.disk_number}</p>
                                </div>
                            </Link>
                        </CardContent>
                    </Card>
                )}

                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                            <Truck className="w-5 h-5" />
                            Informations du mouvement
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Type */}
                        <div className="space-y-2">
                            <Label htmlFor="f-type">Type</Label>
                            {editing ? (
                                <Select value={formData.type} onValueChange={(v) => handleChange('type', v)}>
                                    <SelectTrigger id="f-type" data-testid="movement-type-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {MOVEMENT_TYPES.map(t => (
                                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <p className="text-sm font-medium">{typeLabel}</p>
                            )}
                        </div>

                        {/* Date */}
                        <div className="space-y-2">
                            <Label htmlFor="f-date">Date</Label>
                            {editing ? (
                                <Input id="f-date" type="date" value={formData.date} onChange={(e) => handleChange('date', e.target.value)} data-testid="movement-date-input" />
                            ) : (
                                <p className="text-sm font-medium">{formData.date}</p>
                            )}
                        </div>

                        {/* Address */}
                        <div className="space-y-2">
                            <Label htmlFor="f-adresse">{formData.type === 'reception' ? 'Adresse de réception' : 'Adresse d\'expédition'}</Label>
                            {editing ? (
                                <Input
                                    id="f-adresse"
                                    value={formData.type === 'reception' ? formData.reception_address : formData.expedition_address}
                                    onChange={(e) => handleChange(formData.type === 'reception' ? 'reception_address' : 'expedition_address', e.target.value)}
                                    placeholder="Adresse"
                                    data-testid="movement-address-input"
                                />
                            ) : (
                                <p className="text-sm">{(formData.type === 'reception' ? formData.reception_address : formData.expedition_address) || '-'}</p>
                            )}
                        </div>

                        {/* Delivery Mode */}
                        <div className="space-y-2">
                            <Label htmlFor="f-mode-de-livraison">Mode de livraison</Label>
                            {editing ? (
                                <Select value={formData.delivery_mode} onValueChange={(v) => handleChange('delivery_mode', v)}>
                                    <SelectTrigger id="f-mode-de-livraison" data-testid="movement-delivery-select"><SelectValue placeholder="Non renseigné" /></SelectTrigger>
                                    <SelectContent>
                                        {DELIVERY_MODES.map(m => (
                                            <SelectItem key={m.value || 'empty'} value={m.value}>{m.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <p className="text-sm">{modeLabel}</p>
                            )}
                        </div>

                        {/* Tracking Number */}
                        <div className="space-y-2">
                            <Label htmlFor="f-numero-de-suivi">Numéro de suivi</Label>
                            {editing ? (
                                <Input id="f-numero-de-suivi"
                                    value={formData.tracking_number}
                                    onChange={(e) => handleChange('tracking_number', e.target.value)}
                                    placeholder="Numéro de suivi"
                                    data-testid="movement-tracking-input"
                                />
                            ) : (
                                <p className="text-sm">{formData.tracking_number || '-'}</p>
                            )}
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <Label htmlFor="f-notes">Notes</Label>
                            {editing ? (
                                <Textarea id="f-notes"
                                    value={formData.notes}
                                    onChange={(e) => handleChange('notes', e.target.value)}
                                    placeholder="Notes"
                                    rows={3}
                                    data-testid="movement-notes-input"
                                />
                            ) : (
                                <p className="text-sm">{formData.notes || '-'}</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
